import React, { useEffect, useMemo, useRef } from "react";

import { useDispatch, useSelector } from "react-redux";
import { Polyline } from "react-leaflet";
import _ from "lodash";
import { selectMapPoint, setMapPointSelected } from "redux/actions/index";
import { distance } from "../util/PlaylistUtils";
import { message } from "antd";
import * as turf from "@turf/turf";

const geomColourDict = {
    blue: {
        1: "#94D6FC",
        2: "#629EE5",
        3: "#4765AC",
    },
    default: {
        1: "rgb(127, 127, 127)",
        2: "rgb(127, 127, 127)",
        3: "rgb(127, 127, 127)",
    },
};

const userConfigSelector = (state) => state.userDetails.userConfig;
const currentModeSelector = (state) => state.tool_mode;
const currentRouteGeometrySelector = (state) => state.playlist.selectedRouteGeometry;
const routeIDSelector = (state) => state.playlist.data.routeID;

export const MapGeometryV2 = ({ segment, showRouteAge, mapType, sessionListFiltersActive }) => {
    const polylineRef = useRef();
    const userConfig = useSelector(userConfigSelector);
    const currentMode = useSelector(currentModeSelector);
    const currentRouteGeometry = useSelector(currentRouteGeometrySelector);
    const routeID = useSelector(routeIDSelector);

    const dispatch = useDispatch();

    const bringToTop = () => {
        if (polylineRef.current) {
            polylineRef.current.leafletElement.bringToFront();
        }
    };

    const sendToBack = () => {
        if (polylineRef.current) {
            polylineRef.current.leafletElement.bringToBack();
        }
    };

    const getGeometry = (points) => {
        return points.map((point) => {
            return [point[2], point[1], point[0]];
        });
    };

    const geometry = useMemo(() => {
        return getGeometry(segment.points);
    }, [segment.points]);

    const routeColour = useMemo(() => {
        let routeColour = "rgb(127, 127, 127)";

        if (showRouteAge && segment.latest_session) {
            let colourScheme = null;

            switch (mapType) {
                case "Rail":
                case "Dark":
                case "Aerial":
                case "Detailed":
                case "Light":
                    colourScheme = geomColourDict["blue"];
                    break;
                default:
                    colourScheme = geomColourDict["default"];
                    break;
            }
            let timestamp = segment.latest_session;
            if (timestamp > Date.now() / 1000 - 60 * 60 * 24 * 7 * 6) {
                //6 weeks
                routeColour = colourScheme["1"];
            } else if (timestamp > Date.now() / 1000 - 60 * 60 * 24 * 7 * 12) {
                //12 weeks
                routeColour = colourScheme["2"];
            } else {
                routeColour = colourScheme["3"];
            }
        }

        if (segment && segment.isFilteredOut) {
            routeColour = "#45444a";
        }

        return routeColour;
    }, [mapType, segment, showRouteAge]);

    const routeOpacity = useMemo(() => {
        let routeOpacity = 1;

        if (segment && segment.isFilteredOut) {
            routeOpacity = 0.8;
        }

        return routeOpacity;
    }, [segment]);

    const strokeWeight = 4;

    const onMouseClick = (location) => {
        if (segment && segment.isFilteredOut) {
            if (userConfig.super_admin && !sessionListFiltersActive) {
                message.warning('This track is filtered out by "Flagged sessions"', 2);
            } else {
                message.info("This track is filtered out", 2);
            }
            return null;
        }
        dispatch(setMapPointSelected(true, [location.latlng.lng, location.latlng.lat]));

        if (currentMode) {
            return;
        }
        let orderedPoints = _.sortBy(
            segment.points.map((point) => {
                return {
                    id: point[0],
                    coords: [point[2], point[1]],
                    distance: distance([location.latlng.lng, location.latlng.lat], [point[1], point[2]]),
                };
            }),
            (point) => point.distance,
        );

        let withinCurrentSession = false;

        if (currentRouteGeometry && currentRouteGeometry.length && routeID) {
            const currentSection = _.flatMap(currentRouteGeometry);
            const line = turf.lineString(currentSection.map((point) => [point[1], point[2]]));
            const buffer = turf.buffer(line, 5, { units: "meters" });
            const coordToCheck = turf.point([location.latlng.lng, location.latlng.lat]);
            withinCurrentSession = turf.booleanPointInPolygon(coordToCheck, buffer);
        }

        dispatch(selectMapPoint(orderedPoints[0].id, [location.latlng.lng, location.latlng.lat], false, withinCurrentSession));
    };

    useEffect(() => {
        if (segment.isFilteredOut) {
            sendToBack();
        } else {
            bringToTop();
        }
    }, [segment.isFilteredOut]);

    return (
        <Polyline
            ref={polylineRef.current}
            color={routeColour}
            weight={strokeWeight}
            opacity={routeOpacity}
            positions={geometry}
            onClick={onMouseClick}
        />
    );
};
